import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { YachtDetails } from "@/src/app/Yachts/model";
import { fetch } from "@/src/lib/fetch";
import { Separator } from "@/src/components/ui/separator";
import { ExternalLink } from "lucide-react";
import { useForm, useFormContext } from "react-hook-form";
import { Form } from "@/src/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { emptyOfferYacht, OfferYachtForm } from "../offer-yacht.schema";
import { differenceInDays, format, parseISO, startOfDay, subDays } from "date-fns";
import { Button } from "@/src/components/ui/button";
import { useSubscribe } from "@/src/lib/actions";
import { OfferYacht } from "../../model";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/src/components/ui/dialog";
import { ActionButton } from "@/src/components/wrappers/WithActionBar";
import { OfferForm } from "../offer.schema";
import { useToast } from "@/src/components/ui/use-toast";
import { useOffersStore } from "@/src/stores/offersStore";
import { calculateCurrencyPrice } from "@/src/lib/utils";
import clsx from "clsx";
import { useAmountsStore } from "@/src/stores/offerAmountsStore";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/src/components/ui/tabs";
import { DatesAndPricing } from "./DatesAndPricing";
import { ExtraServices } from "./ExtraServices";
import { PaymentSchedule } from "./PaymentSchedule";
import { Specification } from "./Specification";
import { useExtras } from "./useExtras";
import { Location, Region } from "@/src/app/Locations/model";
import YachtLabel from "@/src/components/ui/yacht-label";
import { useToggle } from "@/src/lib/useToggle";
import { logOfferCreatorAction } from "../../offerCreatorLogs";
import { OfferYachtLogContext } from "../../offerCreatorLogs";

export type YachtPrice = {
  currency: string;
  clientPricePostHelmDiscount: number;
  clientPrice: number;
  listPrice: number;
  commissionPostHelmDiscount: number;
  commission: number;
  deposit: number;
  operatorPayablePrice: number;
  operatorTotalPrice: number;
  extrasPriceInOperatorCurrency: number;
  helmDiscount: number;
  helmDiscountPercentage: number;
  clientPriceApi: number;
  helmCustomDiscountPrice: number;
  startTime: string;
  endTime: string;
  startLocation: Location;
  endLocation: Location;
  charterTerms?: "inclusive" | "exclusive";
  vatPercentage?: string;
  apaPercentage?: string;
  paymentPlan?: { dueDate: string; amount: number; percentage: number }[];
};

export const ChangeYachtDetailsModal = ({
  offerId,
  id,
  onAddClick,
  open,
  setOpen,
  onCancel,
  index,
  autoAdd,
  onUnavailable,
  presetCheckIn,
  presetCheckOut,
}: {
  offerId?: string;
  id: string | null;
  onAddClick: (data: OfferYacht) => void;
  open: boolean;
  setOpen: (open: boolean) => void;
  onCancel?: () => void;
  index: number | null;
  autoAdd?: boolean;
  onUnavailable?: () => void;
  // Dates chosen on the calendar view — override the global "When" filter so the
  // yacht is priced/added for the selected week even when no global dates are set
  // (and without a setYachtsFilters round-trip that SearchBar would clobber).
  presetCheckIn?: string;
  presetCheckOut?: string;
}) => {
  const [yacht, setYacht] = useState<YachtDetails | null>(null);
  const [price, setPrice] = useState<YachtPrice | null>(null);
  const [regionNames, setRegionNames] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<string>("dates-and-pricing");
  const mounted = useRef(false);

  const { yachtsFilters, currencies } = useOffersStore();

  const checkIn = presetCheckIn ?? yachtsFilters?.checkIn;
  const checkOut = presetCheckOut ?? yachtsFilters?.checkOut;

  const context = useFormContext<OfferForm>();
  const offerCurrencyCode = context.watch("description.currency");
  const offerYachts = context.watch("yachts");
  const guests = context.watch("description.guests");

  const toast = useToast();

  const yachtCurrencyCode = price?.currency || "EUR";
  const yachtCurrency = currencies?.find((c) => c.code === yachtCurrencyCode);
  const offerCurrency = currencies?.find((c) => c.code === (offerCurrencyCode ?? "EUR"));

  const calculateCurrency = (amount?: number) => {
    if (!amount) return 0;
    return calculateCurrencyPrice(offerCurrency!, yachtCurrency!, amount ?? 0);
  };

  const [acceptCardPayments, setAcceptCardPayments] = useState(true);

  // offer yacht holds all of the amenities that might be added to the yacht, no matter if checked or not
  // amenities have to be a list of yacht amenities with all of the data

  const defaultAmenitiesDetails = context.getValues("yachts")?.[index!]?.amenitiesDetails;

  const defaultValues = useMemo(() => {
    if (!yacht) return undefined;
    // const yachtFormData = context.getValues("yachts")?.find((y) => y.yacht?.id === yacht?.id);
    let yachtFormData;
    if (index !== null && index !== undefined) {
      yachtFormData = context.getValues("yachts")?.[index];
    }
    const charterType = context.getValues("description.charterType");
    const paymentSteps = yachtFormData?.paymentSteps || yacht?.operator?.paymentSchedule;
    const discountCharterPrice = yachtFormData?.discountCharterPrice ?? 0;
    const offerPricing = yachtFormData?.pricing;
    const startDate = yachtFormData?.startDate ?? checkIn;
    const endDate = yachtFormData?.endDate ?? checkOut;
    const startTime = yachtFormData
      ? yachtFormData.startTime || parseDateIntoTime(yachtFormData?.startDate)
      : getDateByProvider(yacht.provider).start;
    const endTime = yachtFormData
      ? yachtFormData.endTime || parseDateIntoTime(yachtFormData?.endDate)
      : getDateByProvider(yacht.provider).end;
    mounted.current = true;
    if (charterType === "crewed" && !yachtFormData?.cardPayments) {
      setAcceptCardPayments(false);
    } else {
      setAcceptCardPayments(yachtFormData?.cardPayments ?? true);
    }

    // Collapse the operator's default split to a single 100%-on-confirmation
    // payment when the charter is too close for it to make sense: a later step's
    // due date falls on/before an earlier one (the "X days before charter" balance
    // lands before the on-confirmation deposit / today). Only for the operator
    // default — never override a broker's saved/edited schedule.
    const usingOperatorDefault = !yachtFormData?.paymentSteps;
    const stepDue = (step: { type?: string; days?: number }) =>
      step?.type === "before booking date" && startDate
        ? startOfDay(subDays(new Date(startDate), step?.days ?? 0)).getTime()
        : startOfDay(new Date()).getTime();
    // Without a start date every "before booking date" step evaluates as due
    // today, ties with the deposit step, and the whole operator schedule
    // wrongly collapsed to a single 100% payment (adding the yacht before
    // picking dates lost the operator's 30/70). Only judge order once the
    // dates exist.
    const scheduleOutOfOrder =
      usingOperatorDefault &&
      !!startDate &&
      Array.isArray(paymentSteps) &&
      paymentSteps.length > 1 &&
      paymentSteps.some((s, i) => i > 0 && stepDue(s) <= stepDue(paymentSteps[i - 1]));
    const effectiveSteps = scheduleOutOfOrder
      ? [{ ...paymentSteps[0], amount: 100, originalPercentage: 100, days: 0, type: "on confirmation" as const }]
      : paymentSteps;

    return {
      ...emptyOfferYacht,
      securityDeposit: yachtFormData?.pricing?.depositPrice ?? yacht?.securityDepositAmount,
      cardPayments: yachtFormData?.cardPayments
        ? (yachtFormData?.cardPayments ?? true)
        : charterType === "crewed"
          ? false
          : true,
      ignoreAvailability: yachtFormData?.ignoreAvailability ?? emptyOfferYacht.ignoreAvailability,
      brokerNote: yachtFormData?.brokerNote ?? null,
      holdExpiresAt: yachtFormData?.holdExpiresAt ?? null,
      paymentSteps: effectiveSteps?.map((step, i) => ({
        ...step,
        scheduleStep: i,
        operatorAmount: 0,
        originalPercentage: step.originalPercentage ? step.originalPercentage : step.amount,
        isNewOffer: true,
      })),
      discountCharterPrice,
      pricing: offerPricing ? offerPricing : emptyOfferYacht.pricing,
      amenitiesDetails: [],
      //...(yachtFormData?.cardPayments && { cardPayments: yachtFormData?.cardPayments }),
      ...(yachtFormData?.discountCharterPrice && { discountCharterPrice: yachtFormData?.discountCharterPrice }),
      startDate,
      endDate,
      startTime,
      endTime,
      ...(yachtFormData?.startLocation
        ? { startLocation: yachtFormData?.startLocation }
        : { startLocation: yacht?.location }),
      ...(yachtFormData?.endLocation ? { endLocation: yachtFormData?.endLocation } : { endLocation: yacht?.location }),
      customAmenities: yachtFormData?.customAmenities
        ? yachtFormData?.customAmenities
        : emptyOfferYacht?.customAmenities,
      id: yachtFormData?.id,
    };
  }, [yacht]);

  const form = useForm<OfferYachtForm>({
    resolver: zodResolver(OfferYachtForm),
    // Seed with emptyOfferYacht so array fields (amenitiesDetails/customAmenities)
    // are never undefined on the first render — Pricing reads them before the
    // async form.reset(defaultValues) lands, which crashes with quick-add's
    // hidden mount otherwise.
    defaultValues: defaultValues ?? emptyOfferYacht,
    mode: "onTouched",
    reValidateMode: "onChange",
  });

  const yachtAmenities = form.watch("amenitiesDetails");

  useEffect(() => {
    form.register("pricing.yachtCurrencyCode");
  }, [form]);

  useEffect(() => {
    !price && form.reset(defaultValues);
    console.log("RESET");
  }, [defaultValues]);

  const startDate = form.watch("startDate");
  const endDate = form.watch("endDate");
  const startTime = form.watch("startTime");
  const endTime = form.watch("endTime");
  const startLocationId = form.watch("startLocation.id");
  const startLocationNames = form.watch("startLocation.names");
  const endLocationNames = form.watch("endLocation.names");
  const listPrice = form.watch("pricing")?.originalPrice ?? 0;
  const clientPrice = form.watch("pricing")?.clientPriceApi ?? 0;
  const totalPrice = form.watch("pricing")?.totalPrice ?? 0;

  useEffect(() => {
    if (startLocationId) {
      fetch<{ data: Region[] }>(`/api/locations/locations/${startLocationId}/regions`).then((r) => {
        const region = r.data[0];
        if (region) {
          setRegionNames(region.names);
        }
      });
    }
  }, [startLocationId]);

  const { amenities, amenitiesDetails } = useExtras({
    defaultAmenitiesDetails,
    form,
    startDate,
    endDate,
    regionNames,
    startLocationNames,
    endLocationNames,
    yacht,
    offerCurrencyCode,
    guests,
    listPrice,
    clientPrice,
  });

  // load yacht pricing
  useEffect(() => {
    if (startDate && endDate && yacht) {
      if (startDate >= endDate) {
        return;
      }
      setIsLoading(true);
      const { id: toastId } = toast.toast({
        description: <Loader text={"Price data fetching..."} />,
        variant: "default",
        duration: 50000,
      });
      fetch<{ data: YachtPrice; status: string }>(
        `/api/offers/yacht/${yacht.id}/pricing?periodFrom=${format(parseISO(startDate), "yyyy-MM-dd'T'HH:mm:ss'Z'")}&periodTo=${format(parseISO(endDate), "yyyy-MM-dd'T'HH:mm:ss'Z'")}${guests ? `&guests=${guests}` : ""}`,
        "GET",
      )
        .then((res) => {
          toast.dismiss(toastId);
          setIsLoading(false);
          if (res.data) {
            setPrice(res.data);
            // MMK publishes the real instalment plan per offer. On a fresh add
            // (no saved schedule, broker untouched) prefer it over the operator
            // default — each step except the on-confirmation one is pulled
            // 2 weeks EARLIER than MMK's due date so we always collect before
            // the operator's own deadline. Percentages (not amounts) carry
            // across so the split re-applies to OUR charged total.
            const savedSteps =
              index !== null && index !== undefined ? context.getValues("yachts")?.[index]?.paymentSteps : undefined;
            if (
              res.data.paymentPlan?.length &&
              startDate &&
              !savedSteps &&
              !form.getFieldState("paymentSteps").isDirty
            ) {
              const plan = res.data.paymentPlan;
              const start = startOfDay(parseISO(startDate));
              const maxDays = Math.max(0, differenceInDays(start, startOfDay(new Date())));
              const pcts = plan.map((pp) => Math.round(pp.percentage));
              pcts[pcts.length - 1] += 100 - pcts.reduce((a, b) => a + b, 0);
              const planSteps = plan.map((pp, i) => {
                const base = { operatorAmount: 0, isNewOffer: true, scheduleStep: i };
                if (i === 0) {
                  return { ...base, amount: pcts[0], originalPercentage: pcts[0], days: 0, type: "on confirmation" };
                }
                const due = subDays(startOfDay(parseISO(pp.dueDate)), 14);
                const days = Math.min(maxDays, Math.max(0, differenceInDays(start, due)));
                return { ...base, amount: pcts[i], originalPercentage: pcts[i], days, type: "before booking date" };
              });
              form.setValue("paymentSteps", planSteps as any);
            }
            form.setValue("pricing.yachtCurrencyCode", res.data.currency, {
              shouldDirty: true,
              shouldTouch: true,
            });
            // Update the data if needed after its fetched from API
            !!res.data.startTime &&
              form.setValue("startTime", res.data.startTime, {
                shouldDirty: true,
                shouldTouch: true,
              });
            !!res.data.endTime &&
              form.setValue("endTime", res.data.endTime, {
                shouldDirty: true,
                shouldTouch: true,
              });
            !!res.data.startLocation &&
              form.setValue("startLocation", res.data.startLocation, {
                shouldDirty: true,
                shouldTouch: true,
              });
            !!res.data.endLocation &&
              form.setValue("endLocation", res.data.endLocation, {
                shouldDirty: true,
                shouldTouch: true,
              });
            if (!defaultValues?.discountCharterPrice) {
              const price = calculateCurrency(res.data.clientPricePostHelmDiscount);
              const securityDeposit = res.data.deposit; //set deposit price in eur

              form.setValue("discountCharterPrice", price);
              form.setValue("securityDeposit", securityDeposit);
            }
            toast.toast({
              title: "PRICE FETCHED",
              description: "Price data fetched",
              variant: "default",
            });
          } else {
            setPrice(null);
            toast.toast({
              title: "NO PRICE",
              description: "Price data is not available for given dates",
              variant: "destructive",
            });
          }
        })
        .catch(() => setIsLoading(false));
    }
  }, [startDate, endDate, yacht, guests]);

  // fetch yacht
  useEffect(() => {
    const { id: toastId } = toast.toast({
      description: <Loader text={"Fetching data..."} />,
      variant: "default",
      duration: 50000,
    });
    async function getYacht() {
      return await fetch<{ status: string; data: YachtDetails }>("/api/yachts/yacht/" + id)
        .then((res) => {
          setYacht(res.data);
          toast.dismiss(toastId);
          return res.data;
        })
        .catch(() => toast.dismiss(toastId));
    }
    // const yachtFormData = context.getValues("yachts")?.find((y) => y.yacht?.id === id);

    if (index !== null) {
      // The snapshot can be gone by the time the modal mounts — e.g. the offer was
      // just saved (which clears the yachts form array) or the yacht was removed
      // while modal state still pointed at this index. Reading .yacht off the
      // missing entry crashed the whole app to the error boundary ("Cannot read
      // properties of undefined (reading 'yacht')") — fall back to fetching by id.
      const yachtFormData = context.getValues("yachts")?.[index];
      if (yachtFormData?.yacht) {
        setYacht(yachtFormData.yacht);
        toast.dismiss(toastId);
      } else {
        getYacht();
      }
    } else {
      getYacht();
    }

    return () => {
      toast.dismiss(toastId);
    };
  }, []);

  const onSubmit = useCallback(
    form.handleSubmit(
      (data: any) => {
        const actualTotal = data.paymentSteps.reduce((sum: number, step: { amount: number }) => sum + step.amount, 0);
        const expectedTotal = totalPrice;
        const delta = actualTotal - expectedTotal;

        data.paymentSteps.forEach((step: { amount: number }, idx: number) => {
          useAmountsStore.getState().setAmount(yacht?.id ? yacht.id : "", `step-${idx + 1}`, step.amount);
        });

        // HANDLE OPERATOR AMOUNT
        // In quick-add mode the broker finalises the payment schedule on the
        // Configure step, so don't block the add on a schedule delta here.
        if (delta !== 0 && totalPrice && !autoAdd) {
          const sign = delta > 0 ? "greater" : "less";
          const formattedDelta = delta.toFixed(2);
          const formattedExpected = expectedTotal.toFixed(2);

          const message = `Sum of payments must equal the total price (${formattedExpected}). Currently it's ${sign} by ${formattedDelta}.`;

          toast.toast({
            title: "PAYMENT SCHEDULE",
            description: message,
            variant: "destructive",
          });

          form.setError("paymentSteps", {
            type: "manual",
            message,
          });

          return;
        }

        const withYCCode = {
          ...data,
          pricing: {
            ...data.pricing,
            yachtCurrencyCode: data.pricing?.yachtCurrencyCode ?? price?.currency ?? undefined,
          },
        };

        onAddClick({ ...withYCCode, yacht: yacht, amenitiesDetails: yachtAmenities });
      },
      (err, test) => {
        console.log("err", err);
        console.error("FORM ERROR", form.formState.errors, form.getValues());
      },
    ),
    [form, yachtAmenities, yacht, totalPrice],
  );

  const putOnHoldAndSubmit = async () => {
    const onHoldSuccessful = await putOnHold();
    if (onHoldSuccessful) {
      onSubmit();
    }
  };

  const [creatingOption, toggleCreatingOption] = useToggle();
  const putOnHold = async () => {
    toggleCreatingOption();
    try {
      toast.toast({
        title: "Creating option",
        variant: "default",
      });
      const periodFrom = startDate + "T" + startTime + ":00Z";
      const periodTo = endDate + "T" + endTime + ":00Z";
      logOfferCreatorAction({
        eventType: "offer_yacht.create_option_intent",
        offerId,
        meta: {
          yacht_id: yacht?.id,
          period_from: periodFrom,
          period_to: periodTo,
          create_in_api: true,
          email: true,
        },
      });
      await fetch(`/api/options/options?email=true`, "POST", {
        yachtId: yacht?.id,
        createInApi: true,
        periodFrom,
        periodTo,
      });
      toast.toast({
        title: "Option saved",
        variant: "default",
      });
      return true;
    } catch (e: any) {
      console.error(e);
      toast.toast({
        title: "Failed to create an option",
        description: e.message || "An error occurred while saving the option",
        variant: "destructive",
      });
      return false;
    } finally {
      toggleCreatingOption();
    }
  };

  const paymentSchedule = form.watch("paymentSteps");
  const operatorPayablePrice = form.watch("pricing.operatorPayablePrice");
  const updateSchedule = () => {
    if (!totalPrice || !paymentSchedule?.length) return;

    const amountSum = paymentSchedule.reduce((acc, step) => acc + (step.originalPercentage ?? 0), 0);

    const updatedSteps = paymentSchedule.map((step) => {
      if (!step.originalPercentage) return step;
      if (amountSum === 100) {
        const amount = (step.originalPercentage ? step.originalPercentage / 100 : step.amount / 100) * totalPrice;
        return {
          ...step,
          amount: +amount.toFixed(2),
          operatorAmount:
            (step.originalPercentage ? step.originalPercentage / 100 : step.amount / 100) *
            (operatorPayablePrice ? operatorPayablePrice : totalPrice),
        };
      }
      if (step.isNewStep) step.originalPercentage = 0;
      return step;
    });

    form.setValue("paymentSteps", updatedSteps);
  };

  useEffect(() => {
    updateSchedule();
  }, [totalPrice, paymentSchedule?.length]);

  useEffect(() => {
    console.log("Yacht data: ", yacht);
  }, []);

  useSubscribe("offerYacht:add", onSubmit);
  useSubscribe("offerYacht:holdAndAdd", putOnHoldAndSubmit);

  // Quick-add: once the yacht + its pricing have loaded, submit automatically
  // without ever showing the modal. The broker edits everything on Configure.
  const autoAddFired = useRef(false);
  useEffect(() => {
    if (!autoAdd || autoAddFired.current) return;
    if (!yacht || isLoading) return;
    if (!price || !totalPrice) return;
    autoAddFired.current = true;
    onSubmit();
  }, [autoAdd, yacht, price, totalPrice, isLoading]);

  // Safety net: if pricing never resolves (e.g. no availability for the dates)
  // don't leave the quick-add stuck — bail out after a grace period.
  useEffect(() => {
    if (!autoAdd) return;
    const t = setTimeout(() => {
      if (!autoAddFired.current) onUnavailable?.();
    }, 25000);
    return () => clearTimeout(t);
  }, [autoAdd]);

  if (!yacht) return null;

  const logContext: OfferYachtLogContext = {
    offerId,
    offerYachtId: form.getValues("id"),
    yachtId: yacht.id,
    index,
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      onCancel?.();
    }
    setOpen(nextOpen);
  };

  const body = (
    <Form {...form}>
          <div className="flex flex-col gap-6">
            <Separator />
            {yacht?.provider === "Yachtfolio" && !yacht?.media?.length && (
              <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
                No images were captured for this yacht. Re-scrape it from your logged-in Yachtfolio tab so the client
                offer shows photos.
              </div>
            )}
            <div className="flex gap-4">
              {yacht?.media?.[0] ? (
                <img
                  className="w-[176px] h-[108px] rounded-[5px] object-cover"
                  src={yacht.media[0]}
                  alt={yacht?.name}
                />
              ) : (
                <div className="w-[176px] h-[108px] rounded-[5px] bg-slate-100 flex items-center justify-center text-xs text-slate-400">
                  No image
                </div>
              )}
              <div className="flex flex-col gap-1.5">
                <YachtLabel apiDateUpdated={yacht.apiDateUpdated} apiStatus={yacht.apiStatus} />
                <Button
                  asChild
                  variant="link"
                  className="text-default p-0 flex items-center justify-start gap-1.5 text-xl"
                >
                  <a href={buildYachtLink(yacht)} target="_blank" rel="noreferrer">
                    “{yacht.name}” <ExternalLink />
                  </a>
                </Button>
                <span className="text-base">
                  {yacht.model?.name} {"| " + yacht.year}
                </span>
                <span className="text-sm">
                  {!!yacht.maxPeople && `${yacht.maxPeople} people`} {!!yacht.cabins && `• ${yacht.cabins} cabins`}{" "}
                  {!!yacht.bathrooms && `• ${yacht.bathrooms} bathrooms`}
                </span>
                <div className="flex flex-col mt-1">
                  <span className="text-xs text-medium text-slate-500">Operator</span>
                  <span className="text-sm">{yacht.operator?.name}</span>
                </div>
                <div className="flex flex-col mt-1">
                  <span className="text-xs text-medium text-slate-500">Brand</span>
                  <span className="text-sm">
                    {[yacht.visibleInAnchor && "Anchor", yacht.visibleInHelm && "Helm"].filter(Boolean).join(", ")}
                  </span>{" "}
                </div>
              </div>
            </div>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-6">
                <TabsTrigger value="dates-and-pricing">Dates & Pricing</TabsTrigger>
                <TabsTrigger disabled={false} value="extra-services">
                  Extra services
                </TabsTrigger>
                <TabsTrigger value="specification">Specification</TabsTrigger>
                <TabsTrigger value="payment-schedule">Payment schedule</TabsTrigger>
              </TabsList>
              <TabsContent
                forceMount
                className={`flex flex-col gap-6 ${activeTab !== "dates-and-pricing" ? "h-0 overflow-hidden" : ""} `}
                value="dates-and-pricing"
              >
                <DatesAndPricing
                  logContext={logContext}
                  isLoading={isLoading}
                  yacht={yacht}
                  price={price}
                  offerCurrencyCode={offerCurrencyCode}
                  amenities={amenities}
                />
              </TabsContent>
              <TabsContent
                forceMount
                className={`flex flex-col gap-6 ${activeTab !== "extra-services" ? "h-0 overflow-hidden" : ""} `}
                value="extra-services"
              >
                <ExtraServices
                  logContext={logContext}
                  index={index === null ? (!offerYachts?.length ? 0 : offerYachts?.length) : index}
                  yacht={yacht}
                  price={price}
                  offerCurrencyCode={offerCurrencyCode}
                  amenities={amenities}
                  amenitiesDetails={amenitiesDetails}
                />
              </TabsContent>
              <TabsContent
                forceMount
                className={`flex flex-col gap-6 ${activeTab !== "specification" ? "h-0 overflow-hidden" : ""} `}
                value="specification"
              >
                <Specification yacht={yacht} />
              </TabsContent>
              <TabsContent
                forceMount
                className={`flex flex-col gap-6 ${activeTab !== "payment-schedule" ? "h-0 overflow-hidden" : ""} `}
                value="payment-schedule"
              >
                <PaymentSchedule
                  logContext={logContext}
                  acceptCardPayments={acceptCardPayments}
                  setAcceptCardPayments={setAcceptCardPayments}
                  yacht={yacht}
                  price={price}
                  offerCurrencyCode={offerCurrencyCode}
                  amenities={amenities}
                  isLoading={isLoading}
                />
              </TabsContent>
            </Tabs>
            {/* Phase 1 C1: per-yacht client-facing note + hold window — always
                visible, independent of the active tab. */}
            <div className="flex gap-6 border-t pt-4 mt-2">
              <div className="flex flex-col gap-1.5 grow">
                <label className="text-sm font-medium">Broker note (shown to the client)</label>
                <textarea
                  {...form.register("brokerNote")}
                  rows={2}
                  placeholder="Why this yacht fits — appears on the client's offer page"
                  className="rounded-md border border-input bg-white px-3 py-2 text-sm"
                />
              </div>
              <div className="flex flex-col gap-1.5 w-56 shrink-0">
                <label className="text-sm font-medium">Hold until</label>
                <input
                  type="date"
                  {...form.register("holdExpiresAt")}
                  className="rounded-md border border-input bg-white px-3 py-2 text-sm h-10"
                />
                <span className="text-xs text-slate-400">Setting a date marks the yacht as held.</span>
              </div>
            </div>
          </div>
        </Form>
  );

  // Quick-add: keep the whole form (incl. Pricing, which computes totalPrice)
  // mounted so the auto-submit effect can fire, but hidden and non-interactive
  // — no Dialog overlay to block the rest of the page.
  if (autoAdd) {
    return (
      <div
        aria-hidden
        style={{ position: "fixed", left: -99999, top: -99999, width: 0, height: 0, overflow: "hidden", opacity: 0, pointerEvents: "none" }}
      >
        {body}
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className={`sm:max-w-[720px] lg:max-w-[840px] max-h-[calc(100%-80px)] overflow-y-auto`}>
        <DialogHeader>
          <DialogTitle className="text-3xl flex flex-col">Change yacht details for an offer</DialogTitle>
        </DialogHeader>
        {body}
        <DialogFooter>
          <ActionButton disabled={creatingOption} eventKey="offerYacht:holdAndAdd" type="submit">
            On hold & Add to the offer
          </ActionButton>
          <ActionButton eventKey="offerYacht:add" type="submit">
            Add to the offer
          </ActionButton>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const Loader = ({ text }: { text: string }) => {
  return (
    <div className="w-full h-full flex items-center justify-center gap-2 mx-auto">
      <span className="text-base font-medium">{text}</span>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        className={clsx("animate-spin", "w-6 h-6")}
      >
        <path d="M21 12a9 9 0 1 1-6.219-8.56" />
      </svg>
    </div>
  );
};

function buildYachtLink(props: YachtDetails) {
  if (props.provider === "Helm") {
    return `/yachts/${props.id}`;
  }
  if (props.provider === "CYA") {
    return `https://www.cyabrochure.com/ebn/2386/pdobX/${props.externalId}/1`;
  }
  return "TODO:";
}

export function calculateDaysStarted(dateTime1: string, dateTime2: string): number {
  const start = new Date(dateTime1);
  const end = new Date(dateTime2);

  const startDateOnly = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const endDateOnly = new Date(end.getFullYear(), end.getMonth(), end.getDate());

  const differenceInDays = (endDateOnly.getTime() - startDateOnly.getTime()) / (1000 * 60 * 60 * 24);

  return differenceInDays > 0 ? Math.ceil(differenceInDays) : 0;
}

export function calculateWeeksStarted(dateTime1: string, dateTime2: string): number {
  const start = new Date(dateTime1);
  const end = new Date(dateTime2);

  const startDateOnly = new Date(start.getFullYear(), start.getMonth(), start.getDate());
  const endDateOnly = new Date(end.getFullYear(), end.getMonth(), end.getDate());

  const differenceInDays = (endDateOnly.getTime() - startDateOnly.getTime()) / (1000 * 60 * 60 * 24);

  const weeksStarted = Math.ceil(differenceInDays / 7);

  return weeksStarted > 0 ? weeksStarted : 0;
}

export const parseDateIntoDate = (dateString: string) => {
  const date = new Date(dateString);
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const day = String(date.getUTCDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const parseDateIntoTime = (dateString: string) => {
  const date = new Date(dateString);
  const hours = String(date.getUTCHours()).padStart(2, "0");
  const minutes = String(date.getUTCMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
};

export const getDateByProvider = (provider: "NS" | "BM" | "CYA" | "Helm" | "Yachtfolio" | "Ankor") => {
  // Crewed feeds (CYA, Yachtfolio/MYBA) are noon-to-noon by convention when we
  // have no live API times; bareboat providers embark evening / disembark morning.
  if (provider === "CYA" || provider === "Yachtfolio") {
    return {
      start: "12:00",
      end: "12:00",
    };
  } else {
    return {
      start: "17:00",
      end: "09:00",
    };
  }
};
